import { MESApiService, getMomentTz, spParameter } from "@dexteel/mesf-core";
import {
  ChemistrySample,
  ProcessResponse,
  SampleAttributesPayload,
} from "../models/lab.models";

const moment = getMomentTz();

export const getLabProcess = async ({
  ProcessId = null,
  signal,
}: {
  ProcessId: number | null;
  signal?: AbortSignal;
}) => {
  const apiService = new MESApiService();

  const resp = await apiService.callV2(
    "[LAB].[DEGetProcess]",
    [{ name: "ProcessId", value: ProcessId }],
    signal,
  );

  if (resp.ok) {
    const data = resp.data as ProcessResponse;

    const process = data.tables[0].rows[0];
    data.tables[0].rows[0] = {
      ...data.tables[0].rows[0],
      Start: moment.utc(process["Start"]).toDate(),
      End: process.End ? moment.utc(process["End"]).toDate() : null,
      StartTap: process.StartTap
        ? moment.utc(process["StartTap"]).toDate()
        : null,
      ProductionDate: process.ProductionDate
        ? moment.utc(process["ProductionDate"]).toDate()
        : null,
    };
    return {
      ok: true,
      data,
    };
  } else {
    return {
      ok: false,
      message: resp.message,
    };
  }
};

export type UpsertProcessParams = {
  ProcessId: number | null;
  Start: string;
  End: string | null;
};
export const upsertProcess = async ({
  ProcessId,
  Start,
  End,
}: UpsertProcessParams) => {
  const apiService = new MESApiService();

  const parameters = [
    { name: "ProcessId", value: ProcessId },
    { name: "Start", value: Start },
    { name: "End", value: End },
    { name: "IsManual", value: true },
  ];

  return await apiService.callV2("[EAF].[UpsertProcess]", parameters);
};

type UpsertProcessDataParams = {
  processId: number;
  field: string;
  value: boolean | string | number | Date | null;
};

export const upsertProcessData = async ({
  processId,
  field,
  value,
}: UpsertProcessDataParams) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "ProcessId", value: processId },
    { name: field, value: value },
    { name: "IsManual", value: true },
  ];

  return await apiService.callV2("[EAF].[UpsertProcessData]", parameters);
};

type UpsertHeatParams = {
  HeatId: number;
  field: string;
  value: any;
};

export const upsertHeat = async ({
  HeatId,
  field,
  value,
}: UpsertHeatParams) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "HeatId", value: HeatId },
    { name: field, value: value },
  ];

  return await apiService.callV2("[MS].[UpsertHeat]", parameters);
};

export const getLastestProcesses = async () => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [{ name: "ProcessCode", value: "E" }];
  return await apiService.callV2("[RBD].[GetLastestNProcesses]", parameters);
};

export const searchProcesses = async (searchTerm: string) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "SearchTerm", value: searchTerm },
    { name: "MaxResults", value: 100 },
    { name: "AssetId", value: "11" },
  ];

  return await apiService.callV2("[RBD].[SearchProcesses]", parameters);
};

export const getChemistryHeatReport = async (heatId: number | null) => {
  try {
    const sqlParameters = [];
    sqlParameters.push({ name: "@HeatId", value: heatId });

    const apiService = new MESApiService();
    const resp = await apiService.callJSON(
      "[LAB].[GetHeatReport]",
      sqlParameters,
    );
    if (resp.errorMessage !== null)
      return { ok: false, message: resp.errorMessage };
    return {
      ok: true,
      data: resp.result,
    };
  } catch (e: any) {
    return { ok: false, message: e.toString() };
  }
};

interface GetChemistrySamplesParams {
  heatId: number;
  statusCode: string | null;
}

export const getChemistrySamples = async ({
  heatId,
  statusCode,
}: GetChemistrySamplesParams) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [];

  parameters.push({ name: "HeatId", value: heatId });
  parameters.push({ name: "StatusCode", value: statusCode });
  try {
    const resp = await apiService.callV2(
      "[LAB].[GetChemistrySamples]",
      parameters,
    );
    return {
      ok: true,
      data: resp,
    };
  } catch (e: any) {
    return { ok: false, message: e.toString() };
  }
};

export interface ChemistryResultPayload {
  ChemistrySampleId: number | null;
  ElementId: number;
  Value: number | null;
}

export const upsertChemistryResult = async (
  payload: ChemistryResultPayload,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];

  parameters.push({
    name: "ChemistrySampleId",
    value: payload.ChemistrySampleId,
  });
  parameters.push({ name: "ElementId", value: payload.ElementId });
  parameters.push({ name: "Value", value: payload.Value });

  const resp = await apiService.callV2(
    "[LAB].[UpsertChemistryResult]",
    parameters,
  );

  if (resp.ok) {
    return {
      ok: true,
      data: resp.data,
    };
  } else {
    return {
      ok: false,
      message: resp.message,
    };
  }
};

export const upsertSampleAttributes = async (
  payload: SampleAttributesPayload,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];

  parameters.push({
    name: "ChemistrySampleId",
    value: payload.ChemistrySampleId ?? null,
  });
  parameters.push({
    name: "SampleTypeId",
    value: payload.SampleTypeId,
  });
  parameters.push({
    name: "SampleSequence",
    value: payload.SampleSequence,
  });
  parameters.push({
    name: "HeatId",
    value: payload.HeatId ?? null,
  });
  parameters.push({
    name: "DateTimeResults",
    value: payload.DateTimeResults
      ? new Date(payload.DateTimeResults).toISOString()
      : null,
  });
  parameters.push({
    name: "Comments",
    value: payload.Comments,
  });
  parameters.push({
    name: "Technician",
    value: payload.Technician,
  });

  const resp = await apiService.callV2(
    "[LAB].[UpsertChemistrySample]",
    parameters,
  );

  if (resp.ok) {
    return {
      ok: true,
      data: resp.data,
    };
  } else {
    return {
      ok: false,
      message: resp.message,
    };
  }
};

interface CreateChemistryPayload extends Omit<ChemistrySample, "Reported"> {
  Reported?: Date | string | null;
}
export const createSample = async (
  heatId: number | null,
  sample: CreateChemistryPayload,
) => {
  const apiService = new MESApiService();

  const sampleToSend = { ...sample };

  if (sampleToSend.Reported) {
    sampleToSend.Reported = new Date(sampleToSend.Reported).toISOString();
  }

  const sampleWithHeatId = { ...sampleToSend, HeatId: heatId };

  const parameters: spParameter[] = [
    { name: "JsonData", value: JSON.stringify(sampleWithHeatId) },
  ];

  const resp = await apiService.callV2(
    "[LAB].[UpsertChemistryFromDE]",
    parameters,
  );
  if (resp.ok) {
    return {
      ok: true,
      data: resp.data,
    };
  } else {
    return {
      ok: false,
      message: resp.message,
    };
  }
};

export const deleteChemistrySample = async (
  ChemistrySampleId: number | null,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];

  parameters.push({
    name: "ChemistrySampleId",
    value: ChemistrySampleId ?? null,
  });

  const resp = await apiService.callV2(
    "[LAB].[DeleteChemistrySample]",
    parameters,
  );

  if (resp.ok) {
    return {
      ok: true,
      data: resp.data,
    };
  } else {
    return {
      ok: false,
      message: resp.message,
    };
  }
};
